import { useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD = 3;

type Options = {
  min?: number;
  max?: number;
  step?: number;
  integer?: boolean;
  onClick?: () => void;
};

/**
 * Returns an `onMouseDown` handler that turns any element into a horizontal-drag adjuster.
 *
 * Behavior:
 * - Click without movement (< 3px) → calls `opts.onClick` if provided.
 * - Drag past threshold → continuously calls `onChange(newValue)` based on horizontal delta.
 *
 * `getValue` reads the current value at drag-start time (use a ref-getter to avoid stale closures).
 */
export const useDragToAdjust = (getValue: () => number, onChange: (v: number) => void, opts?: Options) => {
  const [pending, setPending] = useState(false);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startVal = useRef(0);

  // Refs so the drag effect doesn't re-run (and reset the threshold) on every
  // parent render. Without this, every onChange triggered a re-render → new
  // onChange/opts identity → effect re-run → local `isDrag` reset → threshold
  // reapplied → values within ±3 of the drag origin became unreachable.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const onMouseDown = (e: React.MouseEvent) => {
    setPending(true);
    setDragging(false);
    startX.current = e.clientX;
    startVal.current = getValue();
  };

  useEffect(() => {
    if (!pending) return;
    let isDrag = false;
    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - startX.current;
      if (!isDrag) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        isDrag = true;
        setDragging(true);
        e.preventDefault();
      }
      const o = optsRef.current;
      const step = o?.step ?? 1;
      let v = startVal.current + delta * step;
      if (o?.min !== undefined) v = Math.max(o.min, v);
      if (o?.max !== undefined) v = Math.min(o.max, v);
      onChangeRef.current(o?.integer === false ? v : Math.round(v));
    };
    const onUp = () => {
      const wasDrag = isDrag;
      setPending(false);
      setDragging(false);
      if (!wasDrag && optsRef.current?.onClick) optsRef.current.onClick();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pending]);

  return { onMouseDown, dragging };
};
